import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { clock, dolumAciklama, dolumEtiketi, price, qty } from '../format'
import type { Fill, Trade } from '../types'

/**
 * Eşleşme listesi — günün tamamı, yeniden eskiye. Sayfalama yok, liste kendi
 * içinde sonsuz kaydırılır.
 *
 * Panel kabuğunu değil yalnız **gövdeyi** render eder: sekme şeridi ve çerçeve
 * `ActivityPanel`in sorumluluğunda.
 *
 * **Sanallaştırılmış:** yalnız görünen satırlar DOM'a çizilir. Günün tamamı
 * (ölçüldüğünde ~900 işlem, gün ilerledikçe daha fazla) her güncellemede
 * yeniden render edilince 270 ms'ye varan donmalar oluyordu. Yükseklik sabit
 * olduğu için hangi satırların göründüğü aritmetikle bulunuyor.
 *
 * Yön sütunu yok: alış/satış yönü EPİAŞ akışında gelmiyor
 * (bkz. docs/gip2logger-redis.md). Uydurmak yerine fiyatı bir önceki işleme
 * göre yukarı/aşağı tik renklendiriyoruz.
 *
 * **Dolumların gösterimi kaynağına göre ikiye ayrılır:**
 *
 * - `Simüle` (gerçek eşleşmeden türedi): AYRI SATIR AÇILMAZ, tetikleyen işlem
 *   satırının `Gölge` sütununda gösterilir. Dolumumuz o işlemin *yerine
 *   geçerdi*; ayrı satır aynı olayı iki kez sayar, toplam hacmi şişirirdi.
 * - `Süpürdük` / `Süpürüldük` (tahta kaynaklı): tetikleyen gerçek işlem YOK —
 *   bunlar zamana göre araya, KENDİ mor satırlarıyla girer (kullanıcı isteği
 *   2026-07-29). Çift sayım da yok: karşılık gelen gerçek işlem satırı hiç
 *   basılmadı.
 */

interface Props {
  trades: Trade[]
  fills: Fill[]
}

/** Liste satırı: gerçek işlem ya da tahta kaynaklı gölge dolum. */
type TapeRow =
  | { kind: 'trade'; trade: Trade }
  | { kind: 'fill'; fill: Fill }

const rowTs = (row: TapeRow) => (row.kind === 'trade' ? row.trade.ts : row.fill.ts)

/** `.tape-row` yüksekliği — CSS'teki değerle AYNI olmalı. */
const ROW_HEIGHT = 20
/** Görünen pencerenin dışında hazır tutulan satır sayısı (kaydırmada boşluk olmasın). */
const OVERSCAN = 12

export const TradeTape = memo(function TradeTape({ trades, fills }: Props) {
  const boxRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [height, setHeight] = useState(0)

  // İşlem ID'si → o işlemde bizim dolan miktarımız. Aynı işlem birden çok gölge
  // emrimizi doldurabilir (aynı fiyattan iki emrimiz varsa), o yüzden toplanıyor.
  // Yalnız `Simüle` dolumlar: tahta kaynaklıların tradeId'si gerçek işlem değil,
  // tüketilen emrin kimliği — onlar aşağıda kendi satırlarını alıyor.
  const golgeDolum = useMemo(() => {
    const map = new Map<string, { quantity: number; side: string }>()
    for (const fill of fills) {
      if ((fill.liquidity ?? 'trade') !== 'trade') continue
      const prev = map.get(fill.tradeId)
      map.set(fill.tradeId, {
        quantity: (prev?.quantity ?? 0) + fill.quantity,
        side: fill.side,
      })
    }
    return map
  }, [fills])

  // Gerçek işlemler + tahta kaynaklı dolumlar, zamana göre yeniden eskiye.
  const rows = useMemo<TapeRow[]>(() => {
    const sentetik = fills.filter((f) => (f.liquidity ?? 'trade') !== 'trade')
    const birlesik: TapeRow[] = [
      ...trades.map((trade) => ({ kind: 'trade' as const, trade })),
      ...sentetik.map((fill) => ({ kind: 'fill' as const, fill })),
    ]
    if (sentetik.length) birlesik.sort((a, b) => rowTs(b) - rowTs(a))
    return birlesik
  }, [trades, fills])

  // Ölçü her render'da yenileniyor (grafikteki ile aynı gerekçe: tek seferlik
  // mount ölçüsü + ResizeObserver, kutu geç boyutlandığında sıfırda kalıyor).
  const measure = () => {
    const el = boxRef.current
    if (!el) return
    const next = el.getBoundingClientRect().height
    setHeight((prev) => (prev === next ? prev : next))
  }
  useLayoutEffect(measure)

  useEffect(() => {
    const el = boxRef.current
    if (!el) return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length === 0])

  if (rows.length === 0) {
    return <div className="tab-empty">Bu kontratta henüz işlem yok.</div>
  }

  const first = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN)
  const count = Math.ceil(height / ROW_HEIGHT) + OVERSCAN * 2
  const slice = rows.slice(first, first + count)

  return (
    <>
      <div className="tape-head">
        <span>Saat</span>
        <span className="num">Fiyat</span>
        <span className="num">Miktar</span>
        <span className="num" title="Bu işlemde gölge emrimizden dolan miktar">
          Gölge
        </span>
      </div>

      <div
        className="tape"
        ref={boxRef}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      >
        {/* Kaydırma yüksekliğini tüm liste belirler; içeride yalnız pencere çizilir. */}
        <div className="tape-spacer" style={{ height: rows.length * ROW_HEIGHT }}>
          <div style={{ transform: `translateY(${first * ROW_HEIGHT}px)` }}>
            {slice.map((row, offset) => {
              // Zebra MUTLAK sıraya göre: `:nth-child` kullanılsaydı desen
              // pencere içindeki konuma bakar, kaydırdıkça ters dönüp titrerdi.
              const index = first + offset
              const alt = index % 2 === 1 ? ' alt' : ''

              if (row.kind === 'fill') {
                // Tahta kaynaklı gölge dolum (Süpürdük/Süpürüldük): karşılık
                // gelen gerçek işlem satırı yok, kendi mor satırı var.
                const { fill } = row
                return (
                  <div
                    className={`tape-row${alt} shadow-row`}
                    key={`f-${fill.orderId}-${fill.tradeId}`}
                    title={`${dolumAciklama(fill.liquidity)} · emir ${fill.orderId}`}
                  >
                    <span className="muted">{clock(fill.ts)}</span>
                    <span className="num">{price(fill.price)}</span>
                    <span className="num">{qty(fill.quantity)}</span>
                    <span className="num shadow-qty">
                      {`${fill.side === 'BUY' ? '▲' : '▼'} ${dolumEtiketi(fill.liquidity)}`}
                    </span>
                  </div>
                )
              }

              const { trade } = row
              const dolum = golgeDolum.get(trade.id)
              return (
                <div
                  className={`tape-row${alt}${dolum ? ' filled' : ''}`}
                  key={`${trade.id}-${trade.ts}`}
                  title={
                    dolum
                      ? `Gölge emrimizden ${qty(dolum.quantity)} lot doldu ` +
                        `(${dolum.side === 'BUY' ? 'alış' : 'satış'}, Simüle)`
                      : undefined
                  }
                >
                  <span className="muted">{clock(trade.ts)}</span>
                  <span className="num">{price(trade.price)}</span>
                  <span className="num">{qty(trade.quantity)}</span>
                  {/* Yön oku rengin tek başına bilgi taşımamasını sağlıyor. */}
                  <span className="num shadow-qty">
                    {dolum ? `${dolum.side === 'BUY' ? '▲' : '▼'} ${qty(dolum.quantity)}` : ''}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
})
